import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  SkipSelf,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { ControlContainer } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import {
  BehaviorSubject,
  concat,
  Observable,
  of,
  pipe,
  Subject,
  Subscription,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

import {
  PurchaseOrderRelationsConstant,
  EstimateRelationsConstant,
  InvoiceRelationsConstant,
} from '@app/core/_constant/relations.constants';
import { KitService } from '@app/core/kits/services/kit.service';
import { RfqService } from '@app/core/quotes';
import { environment } from '@app/env-service';

@Component({
  selector: 'cnc-sku-kit-dropdown',
  templateUrl: './sku-kit-dropdown.component.html',
  styleUrls: ['./sku-kit-dropdown.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (container :ControlContainer) => container,
      deps: [[new SkipSelf(), ControlContainer]],
    },
  ],
})
export class SkuKitDropdownComponent implements OnInit, OnDestroy {
  loading :boolean = false;
  @Input()
  excludeKits :boolean = false;

  @Input()
  sellableProducts :boolean = false;

  @Input()
  controlName :string;

  @Input()
  isKits :boolean = true;

  @Input()
  addTag :boolean = true;

  @Input()
  isRfqs :boolean = false;

  @Input()
  isClientPurchaseOrder :boolean = false;

  @Input()
  isPurchaseOrder :boolean = false;

  @Input()
  isInKitItemsComponent :boolean = false;

  @Input()
  rfqType :string = '';

  @Output()
  selectedItem = new EventEmitter<any>();

  @Output()
  addSelectedItemsEvent = new EventEmitter<any>();

  @Output()
  resetLineItemEvent = new EventEmitter<any>();

  @Input()
  closeOnSelect :boolean = true;

  @Input()
  selectedItems :any[] = [];

  @Input()
  lineItemIndex :number;

  @Input()
  itemRowId :string = '';

  @Input()
  supplierId :number;

  @Input()
  warehouseId :number;

  isOpen :boolean;

  poItems :any[] = [];
  rfq$ :Observable<any[]>;
  rfqLoading = false;
  rfqInput$ = new Subject<string>();

  kit$ :Observable<any[]>;
  kitLoading = false;
  kitInput$ = new Subject<string>();

  private readonly _rfqs = new BehaviorSubject<any>(<any>[]);
  public rfqs$ = this._rfqs.asObservable();

  private readonly _kits = new BehaviorSubject<any>(<any>[]);
  public kits$ = this._kits.asObservable();

  clientConfig :any = environment.clientConfig;
  fallback_path :string;
  url :string = environment.url;

  @Input()
  quotationLineItems :any[] = [];

  selected :any[] = [];
  selectedIds :any[] = [];

  hasItemsAdded :boolean = false;

  @Input()
  forPoForm :boolean = false;

  @Input()
  forInvoiceForm :boolean = false;

  @Input()
  forEstimateForm :boolean = false;

  @Input()
  warehouseGroupId :number;

  @Input()
  ftInvoiceWarehouseGroup :boolean = false;

  rfqs :any = [];
  rfqsBuffer :any = [];
  kits :any = [];
  kitsBuffer :any = [];
  bufferSize :number = 15;
  numberOfItemsFromEndBeforeFetchingMore :number = 15;
  searchTerm :string = '';
  offsetCount :number = 0;
  private searchSku :Subscription;

  @ViewChild('selectComponent') selectComponent :NgSelectComponent;

  constructor(
    private rfqSvc :RfqService,
    private kitSvc :KitService,
    private cd :ChangeDetectorRef,
  ) {}

  ngOnInit() :void {
    this.checkRfqOrKit();
  }

  ngOnDestroy() :void {
    if (this.isRfqs) {
      this._rfqs.unsubscribe();
    } else {
      this._kits.unsubscribe();
    }
    if (this.searchSku) {
      this.searchSku.unsubscribe();
    }
  }

  private changeSource(event :any, name :string) :void {
    event.target.src = name + 'no_image_placeholder.jpg';
  }

  private checkRfqOrKit() :void {
    if (this.isRfqs) {
      this.loadRfqs();
      this.rfq$.subscribe((data) => {
        this.rfqs = data;
        this.rfqsBuffer = this.rfqs.slice(0, this.bufferSize);
        this._rfqs.next(data);
        this.offsetCount += 1;
        this.cd.detectChanges();
      });
    } else {
      this.loadKits();
      this.kit$.subscribe((data) => {
        this.kits = data;
        this.kitsBuffer = this.kits.slice(0, this.bufferSize);
        this._kits.next(data);
        this.offsetCount += 1;
        this.cd.detectChanges();
      });
    }
  }

  trackByFn(item :any) :any {
    return item.id;
  }

  private selectItem(item :any) :void {
    if (!item && item?.id !== 0) {
      this.selectedItem.emit(null);

      return;
    }

    if (item.id) {
      this.selectedItem.emit(item);

      return;
    }
    if (item.id === 0 && item.product_name) {
      const params = this.setParams(item.product_name, 0);
      this.rfqSvc.searchSKUDropdown(params).subscribe((rfqData :any) => {
        if (rfqData.length) {
          this.selectedItem.emit(rfqData[0]);
        }
      });
    }
  }

  customSearchFn(term :string, item :any) :any {
    term = term.toLocaleLowerCase();

    return (
      item.sku.toLocaleLowerCase().indexOf(term) > -1 ||
      item.product_name.toLocaleLowerCase().indexOf(term) > -1
    );
  }

  addTagFn() :any {
    if (!this.addTag) {
      return this.addTag;
    }

    return (term) => {
      return {
        id: 0,
        product_name: term,
        item: { id: 0, product_name: term },
        qty: 1,
        price: 0,
        discount: 0,
        discount_percent: 0,
      };
    };
  }

  private loadRfqs() :void {
    this.rfq$ = concat(
      of([]), // default items
      this.rfqInput$.pipe(
        debounceTime(350),
        distinctUntilChanged(),
        tap(() => (this.rfqLoading = true)),
        switchMap((term) => {
          if (term === null) {
            this.rfqLoading = false;

            return of([]);
          }
          this.searchTerm = term;
          this.offsetCount = 0;
          const params = this.setParams(term, this.offsetCount);

          return this.rfqSvc.searchSKUDropdown(params).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.rfqLoading = false)),
            map((resp :any) => resp),
          );
        }),
      ),
    );
  }

  private loadKits() :void {
    this.kits$ = concat(
      of([]), // default items
      this.kitInput$.pipe(
        distinctUntilChanged(),
        debounceTime(1000),
        tap(() => (this.kitLoading = true)),
        switchMap((term) => {
          this.searchTerm = term;
          this.offsetCount = 0;

          return this.kitSvc.searchKitsDropdown({ sku: term }).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.kitLoading = false)),
            map((resp) => {
              return resp;
            }),
          );
        }),
      ),
    );
  }

  setParams(term :string = '', offset :number = 0) :any {
    const params :any = {
      sku: term,
    };

    if (this.supplierId && this.forPoForm) {
      params.supplierId = this.supplierId;
    }

    if (this.rfqType !== '') {
      params.rfqType = this.rfqType;
      params.warehouseId = this.warehouseId;
    }

    if (this.isRfqs) {
      if (this.excludeKits) {
        params.noKits = true;
      }
      if (this.sellableProducts) {
        params.sellable = true;
      }
      if (this.forPoForm) {
        params.relations = PurchaseOrderRelationsConstant.join(',');
      }
      if (this.forEstimateForm) {
        params.relations = EstimateRelationsConstant.join(',');
      }
      if (this.forInvoiceForm) {
        if (this.ftInvoiceWarehouseGroup) {
          params.warehouse_group_id = this.warehouseGroupId;
          InvoiceRelationsConstant.push('locationStock');
        }
        params.relations = InvoiceRelationsConstant.join(',');
      }

      params.per_page = this.bufferSize;
      params.offset = offset;
    }

    return params;
  }

  get filteredQuotationLineItems() :any[] {
    const selectedItemIds = [];

    this.selectedItems.forEach((selectItem) => {
      if (selectItem.item) {
        selectedItemIds.push(selectItem.item.quotation_line_item_id);
      }
    });

    return this.quotationLineItems.filter(
      (item) => !selectedItemIds.includes(item.quotation_line_item_id),
    );
  }

  add(item :any) :void {
    if (this.selectedIds.includes(item.id)) {
      // const selectedItemIdIndex = this.selectedIds.indexOf(item.id);
      // this.selectedIds.splice(selectedItemIdIndex, 1);
      // this.selected = this.selected.filter((e) => e.id !== item.id);
    } else {
      this.hasItemsAdded = true;
      this.selected.push(item);
      this.selectedIds.push(item.id);
      this.addSelectedItems(item);
    }
  }

  resetSelectedItems() :void {
    // reset
    this.selected = [];
    this.selectedIds = [];
    if (this.hasItemsAdded) {
      this.resetLineItemEvent.emit({});
    }
  }

  addSelectedItems(item :any) :void {
    this.addSelectedItemsEvent.emit({ items: [item] });
  }

  getItemImage(item :any) :string {
    if (item.images?.length && item.images[0]?.thumbnail_url) {
      return this.url + '/file?f=' + item.images[0].thumbnail_path;
    }

    if (
      item?.rfq_attachments?.[0]?.filetype === 'image/jpeg' ||
      item?.rfq_attachments?.[0]?.filetype === 'image/png'
    ) {
      return this.url + '/file?f=' + item.rfq_attachments[0]?.thumburl;
    }

    if (
      item?.attachments?.[0]?.filetype === 'image/jpeg' ||
      item?.attachments?.[0]?.filetype === 'image/png'
    ) {
      return this.url + '/file?f=' + item.attachments[0]?.thumburl;
    }

    return item.photo;
  }

  checkImage(item :any) :boolean {
    return item?.images?.length ||
      item?.rfq_attachments?.[0] ||
      item?.attachments?.[0] ||
      item?.photo
      ? true
      : false;
  }

  onScrollToEnd() :void {
    this.fetchMore();
  }

  onScroll({ end }) :void {
    const bufferLength = this.isRfqs
      ? this.rfqsBuffer.length
      : this.kitsBuffer.length;

    if (this.loading || this.rfqs.length <= bufferLength) {
      return;
    }

    if (end + this.numberOfItemsFromEndBeforeFetchingMore >= bufferLength) {
      this.fetchMore();
    }
  }

  private fetchMore() :void {
    const params = this.setParams(
      this.searchTerm,
      this.offsetCount * this.bufferSize,
    );
    if (this.isRfqs) {
      this.searchSku = this.rfqSvc.searchSKUDropdown(params).subscribe((resp) => {
        this.loading = false;
        this.rfqsBuffer = this.rfqsBuffer.concat(resp);
        this.offsetCount += 1;
        this.cd.detectChanges();
      });
    } else {
      this.searchSku = this.kitSvc.searchKitsDropdown(params).subscribe((resp) => {
        this.loading = false;
        this.kitsBuffer = this.kitsBuffer.concat(resp);
        this.offsetCount += 1;
        this.cd.detectChanges();
      });
    }
  }

  get isUsingOnScrollToEnd() :boolean {
    return (
      this.isClientPurchaseOrder ||
      this.isPurchaseOrder ||
      this.isInKitItemsComponent
    );
  }
}
